/**
 * CSRD Page — ESRS Topics & Materiality Assessments
 *
 * Two sections:
 *   - ESRS Topics: E1-E5, S1-S4, G1 topic list
 *   - Assessments: fund/investee materiality scores table + create dialog
 *   - Materiality Matrix: fund selector -> grid of topics with scores
 *
 * Enterprise+ feature gate.
 *
 * Data from:
 *   GET  /api/v1/impact-core/csrd/topics       — ESRS topic list
 *   GET  /api/v1/impact-core/csrd/assessments   — assessment table
 *   POST /api/v1/impact-core/csrd/assessments   — create assessment
 */

import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  BookOpen,
  ClipboardList,
  Grid3X3,
  Plus,
  X,
  ChevronDown,
  Loader2,
  Lock,
} from 'lucide-react';
import { apiClient } from '@/api/client';
import { useAuth } from '@/contexts/AuthContext';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { useTranslation } from '@/i18n';
import { formatStatus } from '@/utils/formatStatus';
import type { Fund, Investee } from '@/types';

// ─── Types ──────────────────────────────────────────────────────

interface ESRSTopic {
  id: string;
  code: string;
  name: string;
  category: 'environmental' | 'social' | 'governance';
  description: string;
}

interface CSRDAssessment {
  id: number;
  fund_id: number;
  fund_name: string;
  investee_id?: number;
  investee_name?: string;
  topic_code: string;
  topic_name: string;
  assessment_type: string;
  financial_materiality: number;
  impact_materiality: number;
  overall_score: number;
  status: string;
  created_at: string;
}

interface AssessmentCreatePayload {
  fund_id: number;
  investee_id?: number;
  topic_code: string;
  assessment_type: string;
  financial_materiality: number;
  impact_materiality: number;
}

type TabKey = 'topics' | 'assessments' | 'matrix';

const CATEGORY_STYLES: Record<string, { badge: 'success' | 'info' | 'warning'; label: string }> = {
  environmental: { badge: 'success', label: 'Environmental' },
  social: { badge: 'info', label: 'Social' },
  governance: { badge: 'warning', label: 'Governance' },
};

// ─── Main Component ─────────────────────────────────────────────

export function CSRDPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<TabKey>('topics');
  const [showCreate, setShowCreate] = useState(false);
  const [matrixFundId, setMatrixFundId] = useState<string>('');

  // Feature gate: Enterprise+ only
  const tier = user?.subscription_tier ?? 'free';
  const isEnterprise = tier === 'enterprise';

  const topicsQuery = useQuery<ESRSTopic[]>({
    queryKey: ['ic', 'csrd', 'topics'],
    queryFn: async () => {
      const res = await apiClient.get('/api/v1/impact-core/csrd/topics');
      return res.data?.data ?? res.data ?? [];
    },
    enabled: isEnterprise,
  });

  const assessmentsQuery = useQuery<CSRDAssessment[]>({
    queryKey: ['ic', 'csrd', 'assessments'],
    queryFn: async () => {
      const res = await apiClient.get('/api/v1/impact-core/csrd/assessments');
      return res.data?.data ?? res.data ?? [];
    },
    enabled: isEnterprise && (activeTab === 'assessments' || activeTab === 'matrix'),
  });

  const fundsQuery = useQuery<Fund[]>({
    queryKey: ['ic', 'funds'],
    queryFn: async () => {
      const res = await apiClient.get('/api/v1/impact-core/funds/');
      return res.data?.data ?? res.data ?? [];
    },
    enabled: isEnterprise,
  });

  const topics = topicsQuery.data ?? [];
  const assessments = assessmentsQuery.data ?? [];

  // Group topics by category
  const groupedTopics = useMemo(() => {
    const groups: Record<string, ESRSTopic[]> = {};
    for (const topic of topics) {
      const cat = topic.category || 'other';
      if (!groups[cat]) groups[cat] = [];
      groups[cat]!.push(topic);
    }
    return groups;
  }, [topics]);

  // Matrix: assessments for the selected fund, keyed by topic
  const matrixData = useMemo(() => {
    if (!matrixFundId) return [];
    return assessments.filter((a) => a.fund_id === Number(matrixFundId));
  }, [assessments, matrixFundId]);

  // Feature gate screen
  if (!isEnterprise) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-center max-w-md">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-amber-500/10">
            <Lock className="h-6 w-6 text-amber-400" />
          </div>
          <h2 className="text-lg font-semibold text-ic-text-primary">Enterprise+ Feature</h2>
          <p className="mt-2 text-xs text-ic-text-tertiary">
            CSRD/ESRS materiality assessments are available on the Enterprise plan.
            Upgrade to access double materiality analysis, ESRS topic mapping, and
            automated CSRD reporting workflows.
          </p>
          <p className="mt-3 text-xs text-ic-text-tertiary">
            Current plan: <span className="font-medium text-ic-text-secondary capitalize">{tier}</span>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold text-ic-text-primary">CSRD / ESRS</h1>
          <p className="text-xs text-ic-text-tertiary">
            European Sustainability Reporting Standards topics and materiality assessments
          </p>
        </div>
        {activeTab === 'assessments' && (
          <button
            onClick={() => setShowCreate(true)}
            className="flex items-center gap-1.5 rounded-md bg-ic-accent px-3 py-2 text-xs font-medium text-white hover:bg-ic-accent-hover transition-colors"
          >
            <Plus className="h-3.5 w-3.5" />
            New Assessment
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="border-b border-ic-border">
        <div className="flex gap-0">
          <TabBtn label="ESRS Topics" icon={<BookOpen className="h-3.5 w-3.5" />} active={activeTab === 'topics'} onClick={() => setActiveTab('topics')} />
          <TabBtn label="Assessments" icon={<ClipboardList className="h-3.5 w-3.5" />} active={activeTab === 'assessments'} onClick={() => setActiveTab('assessments')} />
          <TabBtn label="Materiality Matrix" icon={<Grid3X3 className="h-3.5 w-3.5" />} active={activeTab === 'matrix'} onClick={() => setActiveTab('matrix')} />
        </div>
      </div>

      {/* Topics Tab */}
      {activeTab === 'topics' && (
        <div className="space-y-6">
          {topicsQuery.isLoading ? (
            <LoadingState />
          ) : topicsQuery.isError ? (
            <ErrorMsg msg="Failed to load ESRS topics." />
          ) : topics.length === 0 ? (
            <EmptyMsg msg="No ESRS topics configured." />
          ) : (
            Object.entries(groupedTopics).map(([category, categoryTopics]) => {
              const style = CATEGORY_STYLES[category];
              return (
                <div key={category}>
                  <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-ic-text-tertiary">
                    {style?.label ?? category}
                  </h3>
                  <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                    {categoryTopics.map((topic) => (
                      <div key={topic.id} className="rounded-lg border border-ic-border bg-ic-card p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="font-mono text-xs font-bold text-ic-accent">{topic.code}</span>
                          <StatusBadge label={style?.label ?? category} variant={style?.badge ?? 'default'} size="sm" />
                        </div>
                        <h4 className="text-xs font-medium text-ic-text-primary">{topic.name}</h4>
                        <p className="mt-1 text-[10px] text-ic-text-tertiary line-clamp-2">{topic.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* Assessments Tab */}
      {activeTab === 'assessments' && (
        <div className="overflow-x-auto rounded-lg border border-ic-border bg-ic-card">
          {assessmentsQuery.isLoading ? (
            <TableSkeleton />
          ) : assessmentsQuery.isError ? (
            <ErrorMsg msg="Failed to load assessments." />
          ) : assessments.length === 0 ? (
            <EmptyMsg msg="No assessments created yet. Click 'New Assessment' to start." />
          ) : (
            <table className="w-full text-xs ic-table">
              <thead>
                <tr className="border-b border-ic-border text-left text-[10px] uppercase tracking-wider text-ic-text-tertiary">
                  <th className="px-4 pb-2 pt-3 font-medium">Fund</th>
                  <th className="px-4 pb-2 pt-3 font-medium">Investee</th>
                  <th className="px-4 pb-2 pt-3 font-medium">Type</th>
                  <th className="px-4 pb-2 pt-3 font-medium">Topic</th>
                  <th className="px-4 pb-2 pt-3 font-medium text-right">Financial</th>
                  <th className="px-4 pb-2 pt-3 font-medium text-right">Impact</th>
                  <th className="px-4 pb-2 pt-3 font-medium text-right">Overall</th>
                  <th className="px-4 pb-2 pt-3 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {assessments.map((a) => (
                  <tr key={a.id} className="border-b border-ic-border/50 last:border-0 hover:bg-ic-hover/50">
                    <td className="py-2.5 px-4 text-xs font-medium text-ic-text-primary">{a.fund_name}</td>
                    <td className="py-2.5 px-4 text-xs text-ic-text-secondary">{a.investee_name ?? '--'}</td>
                    <td className="py-2.5 px-4 text-xs text-ic-text-secondary">{a.assessment_type}</td>
                    <td className="py-2.5 px-4">
                      <span className="font-mono text-[10px] text-ic-accent">{a.topic_code}</span>
                      <span className="ml-1.5 text-xs text-ic-text-secondary">{a.topic_name}</span>
                    </td>
                    <td className="py-2.5 px-4 text-right">
                      <ScoreBadge score={a.financial_materiality} />
                    </td>
                    <td className="py-2.5 px-4 text-right">
                      <ScoreBadge score={a.impact_materiality} />
                    </td>
                    <td className="py-2.5 px-4 text-right font-mono text-xs font-semibold text-ic-text-primary">
                      {a.overall_score.toFixed(1)}
                    </td>
                    <td className="py-2.5 px-4">
                      <StatusBadge
                        label={formatStatus(a.status, t)}
                        variant={a.status === 'completed' ? 'success' : a.status === 'in_progress' ? 'info' : 'default'}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Materiality Matrix Tab */}
      {activeTab === 'matrix' && (
        <div className="space-y-4">
          {/* Fund Selector */}
          <div className="relative max-w-sm">
            <select
              value={matrixFundId}
              onChange={(e) => setMatrixFundId(e.target.value)}
              className="w-full appearance-none rounded-md border border-ic-border bg-ic-input px-3 py-2 pr-8 text-xs text-ic-text-secondary focus:border-ic-accent focus:outline-none"
            >
              <option value="">Select a fund...</option>
              {(fundsQuery.data ?? []).map((f) => (
                <option key={f.id} value={f.id}>{f.name}</option>
              ))}
            </select>
            <ChevronDown className="absolute right-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ic-text-tertiary pointer-events-none" />
          </div>

          {!matrixFundId ? (
            <EmptyMsg msg="Select a fund to view its materiality matrix." />
          ) : assessmentsQuery.isLoading ? (
            <LoadingState />
          ) : matrixData.length === 0 ? (
            <EmptyMsg msg="No assessments found for this fund." />
          ) : (
            <div className="rounded-lg border border-ic-border bg-ic-card p-6">
              <h3 className="mb-4 text-xs font-semibold text-ic-text-primary">
                Double Materiality Matrix
              </h3>
              <p className="mb-4 text-[10px] text-ic-text-tertiary">
                X-axis: Financial Materiality | Y-axis: Impact Materiality | Size: Overall Score
              </p>

              {/* Matrix Grid */}
              <div className="relative h-80 w-full border-l-2 border-b-2 border-ic-border">
                {/* Y-axis label */}
                <span className="absolute -left-6 top-1/2 -translate-y-1/2 -rotate-90 text-[9px] text-ic-text-tertiary whitespace-nowrap">
                  Impact Materiality
                </span>
                {/* X-axis label */}
                <span className="absolute -bottom-5 left-1/2 -translate-x-1/2 text-[9px] text-ic-text-tertiary">
                  Financial Materiality
                </span>

                {/* Grid lines */}
                <div className="absolute inset-0">
                  <div className="absolute left-1/2 top-0 h-full w-px bg-ic-border/30" />
                  <div className="absolute top-1/2 left-0 w-full h-px bg-ic-border/30" />
                </div>

                {/* Quadrant labels */}
                <span className="absolute top-2 left-2 text-[8px] text-ic-text-tertiary">High Impact / Low Financial</span>
                <span className="absolute top-2 right-2 text-[8px] text-ic-text-tertiary">High Impact / High Financial</span>
                <span className="absolute bottom-2 left-2 text-[8px] text-ic-text-tertiary">Low Impact / Low Financial</span>
                <span className="absolute bottom-2 right-2 text-[8px] text-ic-text-tertiary">Low Impact / High Financial</span>

                {/* Data points */}
                {matrixData.map((a) => {
                  const x = (a.financial_materiality / 10) * 100;
                  const y = 100 - (a.impact_materiality / 10) * 100;
                  const size = Math.max(24, Math.min(48, a.overall_score * 5));
                  return (
                    <div
                      key={a.id}
                      className="absolute -translate-x-1/2 -translate-y-1/2 rounded-full bg-ic-accent/40 border border-ic-accent/60 flex items-center justify-center cursor-default group"
                      style={{ left: `${x}%`, top: `${y}%`, width: `${size}px`, height: `${size}px` }}
                      title={`${a.topic_code}: F=${a.financial_materiality} I=${a.impact_materiality}`}
                    >
                      <span className="text-[8px] font-bold text-ic-accent">{a.topic_code}</span>
                      {/* Tooltip */}
                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 hidden group-hover:block z-10 min-w-[120px] rounded bg-ic-sidebar border border-ic-border px-2 py-1.5 shadow-lg">
                        <p className="text-[10px] font-medium text-ic-text-primary">{a.topic_name}</p>
                        <p className="text-[9px] text-ic-text-tertiary">
                          Financial: {a.financial_materiality} | Impact: {a.impact_materiality}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Create Assessment Dialog */}
      {showCreate && (
        <AssessmentCreateDialog
          topics={topics}
          funds={fundsQuery.data ?? []}
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false);
            void queryClient.invalidateQueries({ queryKey: ['ic', 'csrd', 'assessments'] });
          }}
        />
      )}
    </div>
  );
}

// ─── Create Assessment Dialog ───────────────────────────────────

function AssessmentCreateDialog({
  topics,
  funds,
  onClose,
  onCreated,
}: {
  topics: ESRSTopic[];
  funds: Fund[];
  onClose: () => void;
  onCreated: () => void;
}) {
  const [fundId, setFundId] = useState('');
  const [investeeId, setInvesteeId] = useState('');
  const [topicCode, setTopicCode] = useState('');
  const [assessmentType, setAssessmentType] = useState('double_materiality');
  const [financialScore, setFinancialScore] = useState('5');
  const [impactScore, setImpactScore] = useState('5');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const investeesQuery = useQuery<Investee[]>({
    queryKey: ['ic', 'investees'],
    queryFn: async () => {
      const res = await apiClient.get('/api/v1/impact-core/investees/');
      return res.data?.data ?? res.data ?? [];
    },
  });

  const mutation = useMutation({
    mutationFn: async (payload: AssessmentCreatePayload) => {
      const res = await apiClient.post('/api/v1/impact-core/csrd/assessments', payload);
      return res.data;
    },
    onSuccess: () => onCreated(),
    onError: (err: unknown) => {
      const axiosErr = err as { response?: { data?: { detail?: string } } };
      setErrors({ _general: axiosErr.response?.data?.detail ?? 'Failed to create assessment.' });
    },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErrors({});

    if (!fundId) { setErrors({ fund_id: 'Select a fund' }); return; }
    if (!topicCode) { setErrors({ topic_code: 'Select a topic' }); return; }

    const payload: AssessmentCreatePayload = {
      fund_id: Number(fundId),
      investee_id: investeeId ? Number(investeeId) : undefined,
      topic_code: topicCode,
      assessment_type: assessmentType,
      financial_materiality: Number(financialScore),
      impact_materiality: Number(impactScore),
    };

    mutation.mutate(payload);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="w-full max-w-lg rounded-xl border border-ic-border bg-ic-card shadow-[0_20px_40px_rgba(0,0,0,0.16)]">
        <div className="flex items-center justify-between border-b border-ic-border px-6 py-4">
          <h2 className="text-sm font-semibold text-ic-text-primary">Create Assessment</h2>
          <button onClick={onClose} className="text-ic-text-tertiary hover:text-ic-text-primary">
            <X className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 px-6 py-4">
          {errors._general && (
            <div className="rounded-md bg-red-500/10 border border-red-500/20 px-3 py-2 text-xs text-red-400">
              {errors._general}
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <FormField label="Fund *" error={errors.fund_id}>
              <select
                value={fundId}
                onChange={(e) => setFundId(e.target.value)}
                className="w-full rounded-md border border-ic-border bg-ic-input px-3 py-2 text-xs text-ic-text-secondary focus:border-ic-accent focus:outline-none"
              >
                <option value="">Select fund...</option>
                {funds.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
              </select>
            </FormField>
            <FormField label="Investee (optional)">
              <select
                value={investeeId}
                onChange={(e) => setInvesteeId(e.target.value)}
                className="w-full rounded-md border border-ic-border bg-ic-input px-3 py-2 text-xs text-ic-text-secondary focus:border-ic-accent focus:outline-none"
              >
                <option value="">Fund-level assessment</option>
                {(investeesQuery.data ?? []).map((inv) => <option key={inv.id} value={inv.id}>{inv.name}</option>)}
              </select>
            </FormField>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <FormField label="ESRS Topic *" error={errors.topic_code}>
              <select
                value={topicCode}
                onChange={(e) => setTopicCode(e.target.value)}
                className="w-full rounded-md border border-ic-border bg-ic-input px-3 py-2 text-xs text-ic-text-secondary focus:border-ic-accent focus:outline-none"
              >
                <option value="">Select topic...</option>
                {topics.map((t) => <option key={t.id} value={t.code}>{t.code} - {t.name}</option>)}
              </select>
            </FormField>
            <FormField label="Assessment Type">
              <select
                value={assessmentType}
                onChange={(e) => setAssessmentType(e.target.value)}
                className="w-full rounded-md border border-ic-border bg-ic-input px-3 py-2 text-xs text-ic-text-secondary focus:border-ic-accent focus:outline-none"
              >
                <option value="double_materiality">Double Materiality</option>
                <option value="financial_only">Financial Only</option>
                <option value="impact_only">Impact Only</option>
              </select>
            </FormField>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <FormField label="Financial Materiality (0-10)">
              <input
                type="number"
                min="0"
                max="10"
                step="0.5"
                value={financialScore}
                onChange={(e) => setFinancialScore(e.target.value)}
                className="w-full rounded-md border border-ic-border bg-ic-input px-3 py-2 text-xs text-ic-text-primary focus:border-ic-accent focus:outline-none"
              />
            </FormField>
            <FormField label="Impact Materiality (0-10)">
              <input
                type="number"
                min="0"
                max="10"
                step="0.5"
                value={impactScore}
                onChange={(e) => setImpactScore(e.target.value)}
                className="w-full rounded-md border border-ic-border bg-ic-input px-3 py-2 text-xs text-ic-text-primary focus:border-ic-accent focus:outline-none"
              />
            </FormField>
          </div>
        </form>

        <div className="flex items-center justify-end gap-3 border-t border-ic-border px-6 py-4">
          <button
            onClick={onClose}
            className="rounded-md border border-ic-border px-4 py-2 text-xs font-medium text-ic-text-secondary hover:bg-ic-hover transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={mutation.isPending}
            className="rounded-md bg-ic-accent px-4 py-2 text-xs font-medium text-white hover:bg-ic-accent-hover disabled:opacity-50 transition-colors"
          >
            {mutation.isPending ? 'Creating...' : 'Create Assessment'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Helpers ───────────────────────────────────────────────────

function ScoreBadge({ score }: { score: number }) {
  const variant = score >= 7 ? 'danger' : score >= 4 ? 'warning' : 'success';
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 font-mono text-[10px] font-medium ${
      variant === 'danger' ? 'bg-red-500/15 text-red-400 border-red-500/25' :
      variant === 'warning' ? 'bg-amber-500/15 text-amber-400 border-amber-500/25' :
      'bg-emerald-500/15 text-emerald-400 border-emerald-500/25'
    }`}>
      {score.toFixed(1)}
    </span>
  );
}

function TabBtn({
  label,
  icon,
  active,
  onClick,
}: {
  label: string;
  icon: React.ReactNode;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-1.5 px-4 py-2.5 text-xs font-medium border-b-2 transition-colors ${
        active
          ? 'border-ic-accent text-ic-accent'
          : 'border-transparent text-ic-text-tertiary hover:text-ic-text-secondary'
      }`}
    >
      {icon}
      {label}
    </button>
  );
}

function FormField({ label, error, children }: { label: string; error?: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="mb-1 block text-[10px] font-medium uppercase tracking-wider text-ic-text-tertiary">{label}</label>
      {children}
      {error && <p className="mt-1 text-[10px] text-red-400">{error}</p>}
    </div>
  );
}

function LoadingState() {
  return (
    <div className="flex h-48 items-center justify-center">
      <Loader2 className="h-5 w-5 animate-spin text-ic-text-tertiary" />
    </div>
  );
}

function ErrorMsg({ msg }: { msg: string }) {
  return (
    <div className="flex h-48 items-center justify-center rounded-lg border border-ic-border bg-ic-card">
      <p className="text-xs text-red-400">{msg}</p>
    </div>
  );
}

function EmptyMsg({ msg }: { msg: string }) {
  return (
    <div className="flex h-48 items-center justify-center rounded-lg border border-ic-border bg-ic-card">
      <p className="text-xs text-ic-text-tertiary">{msg}</p>
    </div>
  );
}

function TableSkeleton() {
  return (
    <div className="p-4 space-y-2">
      {[1, 2, 3, 4].map((i) => <div key={i} className="h-10 animate-pulse rounded bg-slate-700/40" />)}
    </div>
  );
}
